import type { QueryClient } from '@tanstack/react-query';
import type { PlatformSupportConversationFieldsFragment as Conversation } from '@web-app/generated/platform/graphql';

/** How far Intercom's search index can trail a direct conversation read. */
export const SEARCH_LAG_MS = 30_000;
const KEEP_MS = 5 * 60_000;

/**
 * Direct reads of single conversations, so notifications built from Intercom
 * search can show them before the index catches up. `null` means the
 * conversation is gone or no longer the provider's. Local data only, never an
 * alternative source of authorization.
 */
export type ObservedConversations = Record<
  string,
  { conversation: Conversation | null; observedAt: number }
>;
export const observedKey = (viewerId: string) =>
  ['support-concierge-observed', viewerId] as const;

export function observeConversation(
  queryClient: QueryClient,
  viewerId: string,
  id: string,
  conversation: Conversation | null,
  observedAt = Date.now(),
) {
  queryClient.setQueryData<ObservedConversations>(
    observedKey(viewerId),
    (previous = {}) =>
      // Reads are stamped when requested: one that started earlier but
      // finished later must not replace a newer one.
      (previous[id]?.observedAt ?? -Infinity) > observedAt
        ? previous
        : {
            ...Object.fromEntries(
              Object.entries(previous).filter(
                ([, entry]) => entry.observedAt > observedAt - KEEP_MS,
              ),
            ),
            [id]: { conversation, observedAt },
          },
  );
}

/**
 * Unread conversations from search, corrected by direct reads the search
 * cannot reflect yet. Newer content from search still wins.
 */
export function mergeUnread(
  searched: Conversation[],
  searchedAt: number,
  observed: ObservedConversations,
): Conversation[] {
  const unread = new Map(
    searched
      .filter((conversation) => conversation.read === false)
      .map((conversation) => [conversation.id, conversation]),
  );
  for (const [id, { conversation, observedAt }] of Object.entries(observed)) {
    if (observedAt <= searchedAt - SEARCH_LAG_MS) continue;
    const listed = unread.get(id);
    if (listed && conversation && listed.updatedAt > conversation.updatedAt)
      continue;
    if (conversation?.read === false) unread.set(id, conversation);
    else unread.delete(id);
  }
  return [...unread.values()];
}
